import * as TabsPrimitive from '@radix-ui/react-tabs';
import clsx from 'clsx';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ProductDocumentation } from '~/features/product-details/components/ProductOverview/components/ProductDocumentation/ProductDocumentation';
import { ProductSpecifications } from '~/features/product-details/components/ProductOverview/components/ProductSpecifications/ProductSpecifications';
import { useProductDisplaySpecifications } from '~/features/product-details/components/ProductOverview/hooks/useProductDisplaySpecifications';
import { useProductDocuments } from '~/features/product-details/components/ProductOverview/hooks/useProductDocuments';
import { PRODUCT_TABS_ID } from '~/features/product-details/constants';
import { usePDPActions, usePDPData, usePDPState } from '~/features/product-details/context';
import { ProductTab } from '~/features/product-details/types';
import { RawHtml, Typography } from '~/shared/components';
import { useEvents } from '~/shared/hooks/useEvents';
import { useFrame } from '~/shared/utils';
import { useTranslation } from '~/shared/utils/translation';
import styles from './ProductTabs.module.scss';

type TabProps = React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>;

const Tab: React.FC<TabProps> = ({ className, children, ...rest }) => (
    <TabsPrimitive.Trigger className={clsx(styles.tab, className)} {...rest}>
        <Typography variant="paragraph-S" weight="medium">
            {children}
        </Typography>
    </TabsPrimitive.Trigger>
);

type PanelProps = React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content> & {
    expandable?: boolean;
};

const Panel: React.FC<PanelProps> = ({ className, children, expandable, value, ...rest }) => {
    const { translate } = useTranslation();
    const { productTabToExpand } = usePDPState();
    const { setProductTabToExpand } = usePDPActions();
    const [isExpanded, setIsExpanded] = useState(false);
    const [isClipped, setIsClipped] = useState(true);
    const contentRef = useRef<HTMLDivElement>(null);
    const contentId = `${PRODUCT_TABS_ID}-${value}-content`;

    useEffect(() => {
        if (!expandable || productTabToExpand !== value) return;

        // Expand Panel if it is the target.
        setIsExpanded(true);

        // Once the productTabToExpand value has been acknowledged on the first render
        // we will clear it for the next render.
        setProductTabToExpand(undefined);
    }, [productTabToExpand]);

    useEffect(() => {
        const element = contentRef.current;

        if (!expandable || !element || isExpanded) return;

        const observer = new ResizeObserver(() => {
            /*
             * A hidden panel has no box, so both heights read 0 and would measure as
             * "nothing overflows". Keep the last real measurement rather than dropping the
             * clip while the tab is switched away.
             */
            if (!element.clientHeight) return;

            setIsClipped(element.scrollHeight > element.clientHeight);
        });

        observer.observe(element);

        return () => observer.disconnect();
    }, [expandable, isExpanded]);

    return (
        // We will forceMount our tabs' contents so that the info will be crawlable by search engines.
        <TabsPrimitive.Content
            className={clsx(styles.panel, className)}
            forceMount
            value={value}
            {...rest}
        >
            <Typography as="div" variant="paragraph-M">
                {expandable ? (
                    <div
                        ref={contentRef}
                        id={contentId}
                        /*
                         * Two classes, because the cap and the cut have to be there whether or
                         * not anything overflows — they are what makes the measurement possible,
                         * and leaving the cut conditional let a panel paint its full text for a
                         * frame on the way back from hidden. Only the fade is conditional: it
                         * would otherwise sit under content that ends well short of the cap.
                         * Clipping is visual either way — the whole text stays in the document.
                         */
                        className={clsx(
                            !isExpanded && styles.panelContentCollapsed,
                            !isExpanded && isClipped && styles.panelContentClipped,
                        )}
                    >
                        {children}
                    </div>
                ) : (
                    children
                )}
            </Typography>

            {expandable && (isClipped || isExpanded) && (
                <button
                    type="button"
                    className={styles.panelToggle}
                    aria-controls={contentId}
                    aria-expanded={isExpanded}
                    onClick={() => setIsExpanded((wasExpanded) => !wasExpanded)}
                >
                    <Typography as="span" variant="textLink-M">
                        {isExpanded
                            ? translate('pdp.overview.showLess')
                            : translate('pdp.overview.showMore')}
                    </Typography>
                </button>
            )}
        </TabsPrimitive.Content>
    );
};

type Props = Omit<
    React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root>,
    'id' | 'value' | 'defaultValue' | 'onValueChange'
>;

export const ProductTabs: React.FC<Props> = ({ className, ...rest }) => {
    const { translate } = useTranslation();
    const { data: frame } = useFrame();
    const { product } = usePDPData();
    const { activeProductTab } = usePDPState();
    const { setActiveProductTab } = usePDPActions();
    const { PDPActionEvent } = useEvents(frame);
    const specifications = useProductDisplaySpecifications(product);
    const documents = useProductDocuments();

    // Products we are the sole representative for carry a different safety statement.
    const gpsrText = product.c_ItemSoleRepresentation
        ? frame.productSettings?.gpsrExclusive
        : frame.productSettings?.gpsrDefault;

    const productDescription = useMemo(() => {
        const splitStringByLineBreak = (text: string) => text.split(/\n/g);

        const uspsAsList = product.c_ProductUsp
            ? `<ul style="margin: 0; display: inline-block;">${splitStringByLineBreak(
                  product.c_ProductUsp,
              )
                  .map((item) => `<li>${item}</li>`)
                  .join('')}</ul>`
            : '';

        const guaranteeTitle = translate('pdp.overview.guaranteeText') || 'Garanti';

        const guarantee = product.c_guarantee
            ? `<strong>${guaranteeTitle}</strong>\n${product.c_guarantee}`
            : undefined;

        return [product.shortDescription, uspsAsList, product.longDescription, guarantee]
            .filter(Boolean)
            .join('\n\n');
    }, [product.id]);

    // The GPSR statement is rendered in the Specifications panel, so it alone can warrant the
    // tab: a product may carry the statement without carrying a single specification.
    const hasSpecifications = !!specifications?.length || !!gpsrText;
    const hasDocumentation = !!documents.length;

    const specificationsTitle = translate('pdp.summary.specsTitle');
    const documentationTitle = translate('pdp.summary.documentationTitle');
    const descriptionTitle = translate('pdp.summary.descriptionTitle');

    const tabTitles: Partial<Record<ProductTab, string>> = {
        [ProductTab.DESCRIPTION]: descriptionTitle,
        [ProductTab.SPECIFICATIONS]: specificationsTitle,
        [ProductTab.DOCUMENTATION]: documentationTitle,
    };

    const onTabChange = (tabId: string) => {
        // ASSERTION: We only use ProductTab.* for the tab names so this is guaranteed.
        const tab = tabId as ProductTab;
        const title = tabTitles[tab];

        setActiveProductTab(tab);

        if (title) {
            PDPActionEvent(`Open Tab - ${title}`, `${product.id} | ${product.name}`);
        }
    };

    const renderedTabs = [
        ProductTab.DESCRIPTION,
        ...(hasSpecifications ? [ProductTab.SPECIFICATIONS] : []),
        ...(hasDocumentation ? [ProductTab.DOCUMENTATION] : []),
    ];

    const selectedTab = renderedTabs.includes(activeProductTab)
        ? activeProductTab
        : ProductTab.DESCRIPTION;

    return (
        <TabsPrimitive.Root
            className={clsx(styles.productTabs, className)}
            value={selectedTab}
            onValueChange={onTabChange}
            {...rest}
            id={PRODUCT_TABS_ID}
        >
            <TabsPrimitive.List className={styles.productTabsList}>
                <Tab value={ProductTab.DESCRIPTION}>{descriptionTitle}</Tab>

                {hasSpecifications && (
                    <Tab value={ProductTab.SPECIFICATIONS}>{specificationsTitle}</Tab>
                )}

                {hasDocumentation && (
                    <Tab value={ProductTab.DOCUMENTATION}>{documentationTitle}</Tab>
                )}
            </TabsPrimitive.List>

            <Panel
                key={`${ProductTab.DESCRIPTION}-${product.id}`}
                value={ProductTab.DESCRIPTION}
                expandable
            >
                {!!descriptionTitle && (
                    <Typography className={styles.panelTitle} variant="title-XS" as="h3">
                        {descriptionTitle}
                    </Typography>
                )}

                {!!product.c_ItemHeader && (
                    <Typography
                        className={styles.descriptionHeading}
                        variant="paragraph-M"
                        as="h4"
                        weight="medium"
                    >
                        {product.c_ItemHeader}
                    </Typography>
                )}

                <RawHtml
                    className={styles.productDescription}
                    html={productDescription}
                    shouldSanitizeHtml
                />
            </Panel>

            {hasSpecifications && (
                <Panel
                    key={`${ProductTab.SPECIFICATIONS}-${product.id}`}
                    value={ProductTab.SPECIFICATIONS}
                    expandable
                >
                    {!!specificationsTitle && (
                        <Typography className={styles.panelTitle} variant="title-XS" as="h3">
                            {specificationsTitle}
                        </Typography>
                    )}

                    <ProductSpecifications
                        specifications={specifications ?? []}
                        gpsrText={gpsrText}
                    />
                </Panel>
            )}

            {hasDocumentation && (
                <Panel
                    key={`${ProductTab.DOCUMENTATION}-${product.id}`}
                    value={ProductTab.DOCUMENTATION}
                >
                    {!!documentationTitle && (
                        <Typography className={styles.panelTitle} variant="title-XS" as="h3">
                            {documentationTitle}
                        </Typography>
                    )}

                    <ProductDocumentation />
                </Panel>
            )}
        </TabsPrimitive.Root>
    );
};
